import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

type Config = {
  folder_id: string | null;
  service_account_email: string | null;
  enabled: boolean;
  has_credentials: boolean;
};

type SyncResult = {
  total_files_in_drive: number;
  already_synced: number;
  new_processed: number;
  saved: number;
  needs_review: number;
  errors: number;
  error_details: { file: string; error: string }[];
};

type Status = {
  saved: number;
  needs_review: number;
  errors: number;
  total: number;
};

export function DriveSyncPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const cfgQ = useQuery({
    queryKey: ["drive-config"],
    queryFn: async () => (await api.get<Config>("/drive-sync/config")).data,
  });

  const statusQ = useQuery({
    queryKey: ["drive-status"],
    queryFn: async () => (await api.get<Status>("/drive-sync/status")).data,
  });

  type ReviewItem = {
    id: number;
    drive_file_id: string;
    path: string;
    error: string;
    synced_at: string;
  };

  const reviewQ = useQuery({
    queryKey: ["drive-needs-review"],
    queryFn: async () =>
      (await api.get<ReviewItem[]>("/drive-sync/needs-review")).data,
    enabled: (statusQ.data?.needs_review ?? 0) > 0,
  });

  const [folderId, setFolderId] = useState("");
  const [saFile, setSaFile] = useState<File | null>(null);
  const [saText, setSaText] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  const saveCfgMut = useMutation({
    mutationFn: async () => {
      const json = saFile ? await saFile.text() : saText;
      return (
        await api.post<Config>("/drive-sync/config", {
          folder_id: folderId,
          service_account_json: json,
          enabled,
        })
      ).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drive-config"] });
      setSaFile(null);
      setSaText("");
      setTestMsg("✓ Lưu cấu hình OK");
    },
    onError: (e: any) => {
      setTestMsg(`✗ ${e?.response?.data?.detail ?? e.message}`);
    },
  });

  const testMut = useMutation({
    mutationFn: async () =>
      (await api.post<{ ok: boolean; message: string }>("/drive-sync/test")).data,
    onSuccess: (r) => setTestMsg((r.ok ? "✓ " : "✗ ") + r.message),
  });

  const syncMut = useMutation({
    mutationFn: async () =>
      (await api.post<SyncResult>("/drive-sync/run")).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drive-status"] });
      qc.invalidateQueries({ queryKey: ["input-invoices"] });
    },
  });

  const cfg = cfgQ.data;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-semibold text-slate-900 mb-1">📁 Đồng bộ Google Drive</h1>
      <p className="text-sm text-slate-500 mb-5">
        Cấu hình 1 lần — sau đó mỗi lần upload PDF vào Drive, bấm "Đồng bộ ngay" sẽ
        tự kéo về + parse + lưu hoá đơn theo cấu trúc folder.
      </p>

      <div className="card p-4 mb-4">
        <h2 className="font-medium mb-3">Trạng thái hiện tại</h2>
        {cfgQ.isLoading ? (
          <div className="text-gray-500">Đang tải...</div>
        ) : !cfg?.has_credentials ? (
          <div className="text-sm text-orange-700 bg-orange-50 px-3 py-2 rounded">
            ⚠️ Chưa cấu hình Service Account — xem hướng dẫn bên dưới.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-gray-500 text-xs">Email service account</div>
              <div className="font-mono text-xs break-all">
                {cfg.service_account_email}
              </div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Folder ID</div>
              <div className="font-mono text-xs break-all">{cfg.folder_id}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Trạng thái</div>
              <div>{cfg.enabled ? "✓ Đã bật" : "✗ Tắt"}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs">Đã sync</div>
              <div>
                <b>{statusQ.data?.saved ?? 0}</b> OK •{" "}
                <span className="text-orange-700">
                  {statusQ.data?.needs_review ?? 0}
                </span>{" "}
                cần review •{" "}
                <span className="text-red-600">{statusQ.data?.errors ?? 0}</span> lỗi
              </div>
            </div>
          </div>
        )}

        {cfg?.has_credentials && (
          <div className="mt-3 flex gap-2 flex-wrap">
            <button
              onClick={() => testMut.mutate()}
              disabled={testMut.isPending}
              className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50"
            >
              {testMut.isPending ? "..." : "🔌 Test kết nối"}
            </button>
            <button
              onClick={() => syncMut.mutate()}
              disabled={syncMut.isPending}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {syncMut.isPending ? "⏳ Đang đồng bộ..." : "🔄 Đồng bộ ngay"}
            </button>
          </div>
        )}

        {testMsg && (
          <div className="mt-3 text-sm bg-gray-50 border rounded p-2 whitespace-pre-wrap">
            {testMsg}
          </div>
        )}

        {syncMut.data && (
          <div className="mt-3 text-sm bg-green-50 border border-green-200 rounded p-3">
            <div className="font-medium">Kết quả đồng bộ:</div>
            <ul className="mt-1 ml-4 list-disc text-xs">
              <li>Drive có: {syncMut.data.total_files_in_drive} PDF</li>
              <li>Đã sync trước: {syncMut.data.already_synced}</li>
              <li>Xử lý lần này: {syncMut.data.new_processed}</li>
              <li className="text-green-700">
                ✓ Tự lưu OK: <b>{syncMut.data.saved}</b>
              </li>
              <li className="text-orange-700">
                ⚠️ Cần review (parse thiếu data): {syncMut.data.needs_review}
              </li>
              <li className="text-red-600">
                ✗ Lỗi: {syncMut.data.errors}
              </li>
            </ul>
            {syncMut.data.error_details.length > 0 && (
              <details className="mt-2 text-[10px]">
                <summary className="cursor-pointer">Chi tiết lỗi</summary>
                <ul className="mt-1 ml-4 list-disc">
                  {syncMut.data.error_details.map((e: { file: string; error: string }, i: number) => (
                    <li key={i}>
                      <span className="font-mono">{e.file}</span>: {e.error}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {(statusQ.data?.needs_review ?? 0) > 0 && (
        <div className="bg-white border border-orange-300 rounded p-4 mb-4">
          <h2 className="font-medium mb-2 text-orange-800">
            ⚠️ {statusQ.data?.needs_review} file cần review (parser chưa đọc đủ data)
          </h2>
          <p className="text-xs text-gray-600 mb-2">
            Mở file PDF gốc trên Drive để xem nội dung, sau đó vào trang{" "}
            <a href="/hoa-don-dau-vao" className="text-blue-600 underline">
              Hoá đơn đầu vào
            </a>{" "}
            → bấm "+ Tạo hoá đơn" để nhập tay. PDF gốc đã được tải về server và lưu
            tại{" "}
            <code className="font-mono text-[10px] bg-gray-100 px-1">
              /data/ketoan/invoices
            </code>
            .
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border">
              <thead className="bg-orange-50">
                <tr>
                  <th className="text-left px-2 py-1 border">Đường dẫn Drive</th>
                  <th className="text-left px-2 py-1 border">Lý do</th>
                  <th className="text-left px-2 py-1 border">Sync lúc</th>
                  <th className="text-left px-2 py-1 border">Mở Drive</th>
                </tr>
              </thead>
              <tbody>
                {(reviewQ.data ?? []).map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="border px-2 py-1 font-mono text-[10px] break-all">
                      {r.path}
                    </td>
                    <td className="border px-2 py-1 text-[11px]">{r.error}</td>
                    <td className="border px-2 py-1 text-[10px] text-gray-500">
                      {new Date(r.synced_at).toLocaleString("vi-VN")}
                    </td>
                    <td className="border px-2 py-1">
                      <a
                        href={`https://drive.google.com/file/d/${r.drive_file_id}/view`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline text-[10px]"
                      >
                        Xem PDF →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="card p-4 mb-4">
          <h2 className="font-medium mb-3">
            {cfg?.has_credentials ? "Cập nhật cấu hình" : "Thiết lập lần đầu"}
          </h2>
          <div className="space-y-3 text-sm">
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Folder ID (từ URL Drive)
              </label>
              <input
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
                placeholder="vd: 1Abc...XYZ (URL: drive.google.com/drive/folders/<ID>)"
                className="w-full border rounded px-2 py-1.5 text-xs font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">
                Service Account JSON (chọn file hoặc paste)
              </label>
              <input
                type="file"
                accept=".json,application/json"
                onChange={(e) => {
                  setSaFile(e.target.files?.[0] ?? null);
                  if (e.target.files?.[0]) setSaText("");
                }}
                className="block text-xs mb-1"
              />
              <textarea
                value={saText}
                onChange={(e) => {
                  setSaText(e.target.value);
                  if (e.target.value) setSaFile(null);
                }}
                placeholder='Hoặc paste JSON: {"type":"service_account",...}'
                rows={4}
                className="w-full border rounded px-2 py-1.5 text-[10px] font-mono"
                disabled={!!saFile}
              />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              Bật đồng bộ
            </label>
            <button
              onClick={() => saveCfgMut.mutate()}
              disabled={
                saveCfgMut.isPending ||
                !folderId ||
                (!saFile && !saText.trim())
              }
              className="px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
            >
              {saveCfgMut.isPending ? "Đang lưu..." : "💾 Lưu cấu hình"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm">
        <h2 className="font-medium mb-2">📖 Hướng dẫn thiết lập Service Account</h2>
        <ol className="list-decimal ml-4 space-y-1 text-xs">
          <li>
            Vào{" "}
            <a
              href="https://console.cloud.google.com/projectcreate"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              Google Cloud Console
            </a>{" "}
            → Tạo project mới (tên gì cũng được, vd "ketoan-nghilong").
          </li>
          <li>
            Vào{" "}
            <a
              href="https://console.cloud.google.com/apis/library/drive.googleapis.com"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              Drive API
            </a>{" "}
            → bấm "Enable".
          </li>
          <li>
            Vào{" "}
            <a
              href="https://console.cloud.google.com/iam-admin/serviceaccounts"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              Service Accounts
            </a>{" "}
            → "Create service account" → tên gì cũng được → Done (skip optional steps).
          </li>
          <li>
            Bấm vào service account vừa tạo → tab "Keys" → "Add key" → "Create new key"
            → JSON → tải file .json về.
          </li>
          <li>
            Mở file Drive → tìm folder HOÁ ĐƠN (vd `Drive của tôi/DỮ LIỆU THUẾ/HOÁ ĐƠN`)
            → click chuột phải → Share → Paste email service account (dạng
            <code className="font-mono bg-gray-100 px-1">
              xxx@xxx.iam.gserviceaccount.com
            </code>
            ) → quyền "Viewer".
          </li>
          <li>
            Folder ID = phần cuối URL khi mở folder. Vd URL{" "}
            <code className="font-mono text-[10px] bg-gray-100 px-1">
              https://drive.google.com/drive/folders/1Abc...XYZ
            </code>{" "}
            → ID = <code className="font-mono bg-gray-100 px-1">1Abc...XYZ</code>.
          </li>
          <li>
            Quay lại đây — paste folder ID + upload file JSON → Lưu → Test kết nối →
            Đồng bộ ngay.
          </li>
        </ol>
      </div>
    </div>
  );
}
